import React from 'react';
import CycleMeasurement from '../../../enums/cycle-measurement';
import { Cycle } from '../../../store/models/cycles';
import { ICycleIoTChannelsSettings } from '../../../store/models/types/cycle/ICycleIoTChannelsSettings';

export const getMeasurementFromUnitSource = (
  units?: CycleMeasurement | string | null,
  isCelsius?: boolean | null,
): CycleMeasurement | null => {
  if (units === CycleMeasurement.Celsius || units === CycleMeasurement.Fahrenheit) {
    return units;
  }

  if (typeof units === 'string') {
    const normalizedUnits = units.trim().toLowerCase();

    if (normalizedUnits === 'c' || normalizedUnits === '°c' || normalizedUnits.includes('celsius')) {
      return CycleMeasurement.Celsius;
    }

    if (normalizedUnits === 'f' || normalizedUnits === '°f' || normalizedUnits.includes('fahrenheit')) {
      return CycleMeasurement.Fahrenheit;
    }
  }

  if (typeof isCelsius === 'boolean') {
    return isCelsius ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit;
  }

  return null;
};

export const hasMixedContractControllerUnits = (cycle: Cycle | null, measurement: CycleMeasurement): boolean =>
  cycle?.cycle_controllers?.some((cycleController) => {
    const controllerMeasurement = getMeasurementFromUnitSource(
      cycleController.controller?.units,
      (cycleController.controller as { isCelsius?: boolean })?.isCelsius,
    );

    return controllerMeasurement !== null && controllerMeasurement !== measurement;
  }) ?? false;

// Returns the native measurement unit of the first channel found in the IoT settings,
// or null if none is available. Used to compute unit-aware validation limits.
export const getControllerMeasurement = (
  channelsSettings: ICycleIoTChannelsSettings | null,
): CycleMeasurement | null => {
  for (const channelSettings of Object.values(channelsSettings?.channels ?? {})) {
    const m = getMeasurementFromUnitSource(channelSettings.units);
    if (m !== null) return m;
  }
  return null;
};

export const hasMixedContractChannelUnits = (
  channelsSettings: ICycleIoTChannelsSettings | null,
  measurement: CycleMeasurement,
): boolean =>
  Object.values(channelsSettings?.channels ?? {}).some((channelSettings) => {
    const channelMeasurement = getMeasurementFromUnitSource(channelSettings.units);

    return channelMeasurement !== null && channelMeasurement !== measurement;
  });

const UnitMismatchWarning: React.FC = () => (
  <div className="mb-4 flex w-full border-l-4 border-warning bg-warning bg-opacity-[10%] px-4 py-3 text-sm text-black dark:bg-opacity-20 dark:text-white">
    <div className="mr-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning text-xs font-semibold text-white">
      i
    </div>
    <p className="leading-relaxed">
      Due to unit conversion and integer rounding, some displayed values may drift by plus or minus 1 degree after
      saving or reloading.
    </p>
  </div>
);

export default UnitMismatchWarning;
